import {useTheme as useStyledTheme} from 'styled-components';

import 'styled-components';

// Override styled components theme
declare module 'styled-components' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  export interface DefaultTheme extends ThemeT {}
}

export const colors = {
  neutral000: 'rgba(255, 255, 255, 0.7)',
  neutral100: '#FFFFFF',
  neutral150: '#F5F4F6',
  neutral200: '#2F30710D',
  neutral300: '#2F307129',
  neutral500: '#1A181880',
  neutral600: '#8D8C8C',
  neutral800: '#1A1818',
  neutral850: '#494954',
  neutral900: '#000000',

  primary100: '#F3F4FC',
  primary150: '#D9DBE8',
  primary200: '#C5C8E8',
  primary250: '#EEEFFA',
  primary300: '#E0E2F5',
  primary400: '#8E91B0',
  primary450: 'rgba(47, 48, 113, 0.8)',
  primary500: '#2F3071',

  secondary100: '#EEEFFB',
  secondary200: '#E0E2F5',
  secondary400: '#7468D6',

  warning500: '#FDAF01',
  warning600: '#F499AF',
  danger500: '#E10101',
  success500: '#00CD39',
  blue300: '#D9DBE8',
  blue400: 'rgba(142, 145, 176, 1)',
  violet600: '#7468D6',

  primary550: '#262757',
  primary600: '#1C1D42',
  violet300: '#A8A4E8',
  violet500: '#6559C9',
  violet700: '#5C52B8',

  /* powierzchnie */
  surface: '#FFFFFF',
  surfaceMuted: '#F8F9FC',
  surfaceSunken: '#F3F4FC',

  border: '#E2E4F0',
  borderStrong: '#CDD0E4',

  /* tekst */
  textPrimary: '#1A1B33',
  textSecondary: '#55586F',
  textMuted: '#8689A3',
  textOnBrand: '#FFFFFF',

  /* statusy - miekkie tla */
  successSoft: '#E7FAEE',
  success600: '#00A32E',
  warningSoft: '#FFF6E0',
  dangerSoft: '#FDECEC',
  infoSoft: '#EEEFFA',
} as const;

export const gradients = {
  brand: 'linear-gradient(135deg, #2F3071 0%, #493F83 55%, #7468D6 100%)',
  brandHover: 'linear-gradient(135deg, #262757 0%, #3F3675 55%, #6559C9 100%)',
  accent: 'linear-gradient(135deg, #7468D6 0%, #A8A4E8 100%)',
  surface: 'linear-gradient(160deg, #FFFFFF 0%, #F5F6FB 100%)',
  page: 'linear-gradient(180deg, #F7F8FC 0%, #F3F4FC 100%)',
  /** Naglowki / hero */
  hero: 'linear-gradient(135deg, rgba(47,48,113,0.96) 0%, rgba(73,63,131,0.94) 50%, rgba(116,104,214,0.92) 100%)',
  /** Szklana warstwa (glassmorphism) */
  glass:
    'linear-gradient(135deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.55) 100%)',
  glow: 'radial-gradient(60% 60% at 50% 0%, rgba(116,104,214,0.2) 0%, rgba(116,104,214,0) 100%)',
  shimmer:
    'linear-gradient(90deg, rgba(47,48,113,0) 0%, rgba(116,104,214,0.22) 50%, rgba(47,48,113,0) 100%)',
  /** Separator z zanikaniem */
  divider:
    'linear-gradient(90deg, rgba(47,48,113,0) 0%, rgba(47,48,113,0.14) 50%, rgba(47,48,113,0) 100%)',
} as const;

export const elevation = {
  none: 'none',
  xs: '0 1px 2px rgba(47, 48, 113, 0.06)',
  sm: '0 2px 8px rgba(47, 48, 113, 0.06), 0 1px 2px rgba(47, 48, 113, 0.04)',
  md: '0 6px 20px rgba(47, 48, 113, 0.08), 0 2px 6px rgba(47, 48, 113, 0.04)',
  lg: '0 14px 38px rgba(47, 48, 113, 0.12), 0 4px 12px rgba(47, 48, 113, 0.06)',
  xl: '0 28px 64px rgba(47, 48, 113, 0.16), 0 8px 20px rgba(47, 48, 113, 0.08)',
  brand: '0 10px 26px rgba(47, 48, 113, 0.28)',
  brandHover: '0 14px 34px rgba(47, 48, 113, 0.34)',
  accent: '0 10px 26px rgba(116, 104, 214, 0.28)',
  /** Wewnetrzny highlight */
  inset: 'inset 0 1px 0 rgba(255, 255, 255, 0.5)',
  focus: '0 0 0 3px rgba(116, 104, 214, 0.26)',
} as const;

export const motion = {
  duration: {
    instant: '90ms',
    fast: '150ms',
    base: '240ms',
    slow: '380ms',
    slower: '600ms',
  },
  easing: {
    standard: 'cubic-bezier(0.4, 0, 0.2, 1)',
    out: 'cubic-bezier(0.22, 1, 0.36, 1)',
    in: 'cubic-bezier(0.55, 0, 1, 0.45)',
    /** Lekkie sprezynowanie */
    spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  },
} as const;

export const lightTheme = {
  colors: colors,
  gradients: gradients,
  elevation: elevation,
  motion: motion,
  spacing: {
    xs: 4,
    s: 8,
    m: 16,
    l: 24,
    xl: 40,
    xxl: 50,
  },
  breakpoints: {
    phone: 0,
    tablet: 768,
  },
  borderRadii: {
    xs: 6,
    s: 12,
    m: 16,
    l: 24,
    xl: 40,
    xxl: 50,
    pill: 999,
  },
  shadow: {
    shadow1: elevation.md,
    shadow2: elevation.lg,
  },
};

// Create dark theme
// export const darkTheme = createTheme({
//   ...lightTheme,
//   colors: {
//     ...lightTheme.colors,
//     mainBackground: palette.black,
//     cardPrimaryBackground: palette.black,
//   },
// });

export type ThemeT = typeof lightTheme;

export type ThemeVariantT = 'light' | 'dark';

export const useTheme = () => useStyledTheme();
